import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, PrimaryButton, GhostButton, Field, Input, Select, Textarea, DemoBadge } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { toast } from "sonner";
import { Sparkle, FileArrowDown, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import SourceVerifyModal from "@/components/SourceVerifyModal";

export default function CandidateReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cand, setCand] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const { user, can, perms } = useAuth();
  const canEdit = can("candidate_opportunities", "update");
  const canImport = perms?.can_import_candidates ?? (user?.role && ["owner","admin","tender_analyst","opportunity_manager"].includes(user.role));
  const canAIScore = ["owner","admin","tender_analyst"].includes(user?.role);

  const load = () => { api.get(`/collections/candidate_opportunities/${id}`).then((r) => { setCand(r.data); setForm(r.data); }); };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try { await api.patch(`/collections/candidate_opportunities/${id}`, { data: form }); toast.success("Saved"); setEditing(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const score = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/score-candidate", { candidate_id: id });
      toast.success(`AI Score: ${r.data.score ?? "—"}`);
      load();
    } catch (e) { toast.error("AI scoring failed"); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!window.confirm(`Import "${cand.candidate_name}" into Opportunities? This cannot be undone.`)) return;
    setImporting(true);
    try {
      const r = await api.post("/candidates/import", { candidate_id: id, confirm: true });
      toast.success("Imported. Redirecting to dossier…");
      nav(`/opportunities/${r.data.opportunity.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  if (!cand) return <div className="p-8 text-zinc-500 text-xs">Loading…</div>;

  const isImported = cand.review_status === "Imported";

  // --- Search Intelligence V2 (deterministic, non-AI) metadata (conditional) ---
  const v2Flags = Array.isArray(cand.manual_review_flags) ? cand.manual_review_flags : [];
  const v2Codes = Array.isArray(cand.candidate_codes_to_verify) ? cand.candidate_codes_to_verify : [];
  const hasV2Scores = cand.product_match_confidence != null || cand.cpv_match_confidence != null || cand.keyword_match_confidence != null;
  const hasV2 = cand.search_match_confidence != null || cand.manual_review_priority != null
    || cand.deadline_confidence != null || cand.manual_download_required != null
    || v2Flags.length > 0 || v2Codes.length > 0 || hasV2Scores;

  // --- V2 import safeguards (mirror backend; backend is source of truth) ---
  const isHistorical = cand.review_status === "Historical Example"
    || cand.active_status === "Historical Example / Not Active";
  const needsVerification = !isHistorical
    && (cand.cannot_import_as_opportunity === true || cand.requires_web_verification === true);
  const importBlocked = isImported || isHistorical || needsVerification;

  return (
    <div>
      <PageHeader
        overline={`Candidate · ${cand.opportunity_type || "Tender"}`}
        title={<span>{cand.candidate_name}<DemoBadge show={cand.is_demo} /></span>}
        sub={`${cand.country || ""} · ${cand.product_category || ""}`}
        actions={
          <>
            <StatusPill value={cand.review_status} size="md" />
            {canAIScore && (
              <GhostButton onClick={score} disabled={busy} data-testid="ai-score-btn">
                <Sparkle size={12} className="inline mr-1" /> {busy ? "Scoring…" : "AI Score"}
              </GhostButton>
            )}
            {editing ? (
              <>
                <GhostButton onClick={() => { setForm(cand); setEditing(false); }}>Cancel</GhostButton>
                <PrimaryButton onClick={save}>Save</PrimaryButton>
              </>
            ) : (
              canEdit && <GhostButton onClick={() => setEditing(true)} data-testid="cand-edit-btn">Edit</GhostButton>
            )}
            {canImport && needsVerification && (
              <GhostButton onClick={() => setShowVerify(true)} data-testid="cand-verify-source-btn">
                <ShieldCheck size={12} className="inline mr-1" /> Mark Source Verified
              </GhostButton>
            )}
            {canImport && (
              <PrimaryButton onClick={doImport} disabled={importBlocked || importing} data-testid="cand-import-btn">
                <FileArrowDown size={12} className="inline mr-1" /> {isImported ? "Imported" : importing ? "Importing…" : "Import to Opportunities"}
              </PrimaryButton>
            )}
          </>
        }
      />

      {(needsVerification || isHistorical) && (
        <div className="px-8 pt-6">
          <div className={`border px-4 py-3 text-xs ${isHistorical ? "border-white/15 bg-white/5 text-zinc-300" : "border-[#FFB300]/40 bg-[#FFB300]/5 text-[#FFB300]"}`}
            data-testid="cand-verification-banner">
            {isHistorical
              ? "Historical Example — reference only. This record can never be imported as an Opportunity."
              : "Source verification required before import. Confirm the official source/status/deadline (or accept the manual-download requirement), then import."}
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 p-8">
        <aside className="col-span-12 lg:col-span-3 lg:sticky lg:top-6 lg:self-start space-y-3">
          <Card className="p-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Suggested Fit</div>
            <div className="font-heading font-bold text-5xl text-[#FFB300] tracking-tighter tabular">{cand.suggested_fit_score ?? "—"}</div>
            <div className="text-[10px] text-zinc-500 mt-1 tracking-widest uppercase">/ 100</div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
              <M label="Deadline" value={cand.deadline} />
              <M label="Q&A" value={cand.qa_deadline} />
              <M label="Value" value={cand.estimated_value ? `${cand.currency || ""} ${cand.estimated_value}` : null} />
              <M label="Quantity" value={cand.quantity} />
              <M label="Source" value={cand.source_portal} />
              <M label="Buyer" value={cand.buyer_contracting_authority} />
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Duplicate Check</div>
            <StatusPill value={cand.duplicate_check} size="md" />
          </Card>
        </aside>

        <div className="col-span-12 lg:col-span-9 space-y-6">
          {cand.ai_scored_at && (
            <Section title="AI Triage Score">
              <div className="border border-[#FFB300]/30 p-4" data-testid="candidate-ai-score-panel">
                <div className="flex items-center gap-4 mb-3">
                  <div className="font-heading font-bold text-4xl text-[#FFB300] tracking-tighter tabular" data-testid="cand-ai-score">{cand.ai_score ?? "—"}</div>
                  <div className="text-[10px] text-zinc-500 tracking-widest uppercase">/ 100</div>
                  {cand.ai_score_label && <StatusPill value={cand.ai_score_label} size="md" />}
                </div>
                {cand.ai_score_reasoning && <Pre>{cand.ai_score_reasoning}</Pre>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1">Positive Signals</div>
                    {(cand.ai_score_positive_signals || []).length > 0
                      ? <ul className="text-[11px] text-[#00E676] space-y-0.5 list-disc list-inside" data-testid="cand-ai-positive">{cand.ai_score_positive_signals.map((s, i) => <li key={i}>{s}</li>)}</ul>
                      : <span className="text-zinc-600 italic text-[11px]">none</span>}
                  </div>
                  <div>
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1">Risk Flags</div>
                    {(cand.ai_score_risk_flags || []).length > 0
                      ? <ul className="text-[11px] text-[#FF3B30] space-y-0.5 list-disc list-inside" data-testid="cand-ai-risks">{cand.ai_score_risk_flags.map((s, i) => <li key={i}>{s}</li>)}</ul>
                      : <span className="text-zinc-600 italic text-[11px]">none</span>}
                  </div>
                </div>
                {cand.ai_score_recommended_next_action && (
                  <div className="mt-3">
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1">Recommended Next Action</div>
                    <div className="text-[11px] text-zinc-200" data-testid="cand-ai-next-action">{cand.ai_score_recommended_next_action}</div>
                  </div>
                )}
                <div className="text-[10px] text-zinc-600 font-mono-data mt-3 pt-2 border-t border-white/10">
                  Scored {cand.ai_scored_at ? new Date(cand.ai_scored_at).toLocaleString() : ""}{cand.ai_scored_by_email ? ` · ${cand.ai_scored_by_email}` : ""}
                </div>
              </div>
            </Section>
          )}
          {hasV2 && (
            <Section title="Search Intelligence V2">
              <div className="border border-white/10 p-4 space-y-4" data-testid="candidate-v2-panel">
                <div className="flex flex-wrap items-center gap-2">
                  {cand.search_match_confidence && <StatusPill value={cand.search_match_confidence} size="md" />}
                  {cand.manual_review_priority && (
                    <span className="text-[10px] tracking-widest uppercase border border-white/15 px-2 py-1 text-zinc-300" data-testid="cand-v2-priority">
                      Review Priority: <span className="text-[#FFB300]">{cand.manual_review_priority}</span>
                    </span>
                  )}
                  {cand.deadline_confidence && (
                    <span className="text-[10px] tracking-widest uppercase border border-white/15 px-2 py-1 text-zinc-300" data-testid="cand-v2-deadline-confidence">
                      Deadline Confidence: <span className="text-zinc-100">{cand.deadline_confidence}</span>
                    </span>
                  )}
                </div>

                {cand.manual_download_required ? (
                  <div className="border border-[#FFB300]/30 bg-[#FFB300]/5 px-3 py-2" data-testid="cand-v2-manual-download">
                    <div className="text-[9px] tracking-widest uppercase text-[#FFB300] mb-0.5">Manual Download Required</div>
                    <div className="text-[11px] text-zinc-300">{cand.manual_download_reason || "Full tender documents are hosted on the buyer portal / TED document page; not directly downloadable from notice metadata."}</div>
                    {cand.document_page_url && (
                      <a href={cand.document_page_url} target="_blank" rel="noreferrer" className="text-[#FFB300] underline text-[11px] break-all" data-testid="cand-v2-document-page-link">{cand.document_page_url}</a>
                    )}
                  </div>
                ) : null}

                {hasV2Scores && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cand-v2-scores">
                    <M label="Product Match" value={cand.product_match_confidence} />
                    <M label="Keyword Match" value={cand.keyword_match_confidence} />
                    <M label="CPV Match" value={cand.cpv_match_confidence} />
                    <M label="Region Match" value={cand.country_region_match} />
                    <M label="Deadline Conf." value={cand.deadline_confidence_score} />
                    <M label="Eligibility Risk" value={cand.eligibility_risk_score} />
                    <M label="Sample Burden" value={cand.sample_testing_burden_score} />
                    <M label="Duplicate Risk" value={cand.duplicate_risk_score} />
                  </div>
                )}

                {v2Flags.length > 0 && (
                  <div>
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1.5">Manual Review Flags</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="cand-v2-flags">
                      {v2Flags.map((f, i) => (
                        <span key={i} className="text-[10px] border border-[#FF3B30]/40 text-[#FF6B61] px-2 py-0.5">{f}</span>
                      ))}
                    </div>
                  </div>
                )}

                {v2Codes.length > 0 && (
                  <div>
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1.5">Candidate Codes — To Verify</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="cand-v2-codes">
                      {v2Codes.map((c, i) => (
                        <span key={i} className="text-[10px] font-mono-data border border-white/15 text-zinc-300 px-2 py-0.5">{c}</span>
                      ))}
                    </div>
                    <div className="text-[10px] text-zinc-600 italic mt-1.5">CPV / category codes are candidate values pending official verification, not confirmed mappings.</div>
                  </div>
                )}

                {((Array.isArray(cand.matched_keywords) && cand.matched_keywords.length > 0) ||
                  (Array.isArray(cand.matched_exact_phrases) && cand.matched_exact_phrases.length > 0) ||
                  (Array.isArray(cand.matched_native_keywords) && cand.matched_native_keywords.length > 0) ||
                  (Array.isArray(cand.matched_cpv_codes) && cand.matched_cpv_codes.length > 0) ||
                  (Array.isArray(cand.exclusion_hits) && cand.exclusion_hits.length > 0)) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-white/10 pt-3" data-testid="cand-v2-matched">
                    <ChipGroup label="Matched Keywords" items={cand.matched_keywords} testid="cand-v2-matched-keywords" />
                    <ChipGroup label="Matched Exact Phrases" items={cand.matched_exact_phrases} testid="cand-v2-matched-phrases" />
                    <ChipGroup label="Matched Native-Language" items={cand.matched_native_keywords} testid="cand-v2-matched-native" />
                    <ChipGroup label="Matched CPV (to verify)" items={cand.matched_cpv_codes} mono testid="cand-v2-matched-cpv" />
                    <ChipGroup label="Exclusion Hits" items={cand.exclusion_hits} tone="red" testid="cand-v2-exclusion-hits" />
                  </div>
                )}

                <div className="border-t border-white/10 pt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]" data-testid="cand-v2-links">
                  <LinkRow label="Official Source" url={cand.official_tender_link} testid="cand-v2-official-link" />
                  <LinkRow label="TED Notice" url={cand.ted_notice_link} testid="cand-v2-ted-link" />
                  <LinkRow label="Buyer Portal" url={cand.buyer_portal_link} testid="cand-v2-buyer-link" />
                  <KV label="Document Access" value={cand.document_access_type} />
                  {cand.deadline_raw_text && <KV label="Deadline (raw text)" value={cand.deadline_raw_text} />}
                </div>

                {(cand.source_verification_status || cand.requires_web_verification || cand.cannot_import_as_opportunity) && (
                  <div className="border-t border-white/10 pt-3" data-testid="cand-v2-verification">
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1.5">Source Verification</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusPill value={cand.source_verification_status || "Unverified"} />
                      {cand.source_verified_by_email && (
                        <span className="text-[10px] text-zinc-500 font-mono-data">by {cand.source_verified_by_email}{cand.source_verified_at ? ` · ${new Date(cand.source_verified_at).toLocaleString()}` : ""}</span>
                      )}
                    </div>
                    {cand.source_verification_notes && <div className="text-[11px] text-zinc-300 mt-1.5">{cand.source_verification_notes}</div>}
                  </div>
                )}

                {(cand.possible_duplicate || ["Possible Duplicate", "Duplicate"].includes(cand.duplicate_check)) && (
                  <div className="border-t border-white/10 pt-3" data-testid="cand-v2-duplicate">
                    <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1.5">Possible Duplicate</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusPill value={cand.duplicate_check} />
                      {(cand.duplicate_of_candidate_id || cand.possible_duplicate_of) && (
                        <a href={`/candidates/${cand.duplicate_of_candidate_id || cand.possible_duplicate_of}`} className="text-[#FFB300] underline text-[11px]" data-testid="cand-v2-duplicate-link">View matched record</a>
                      )}
                    </div>
                    {cand.duplicate_reason && <div className="text-[11px] text-zinc-300 mt-1.5">{cand.duplicate_reason}</div>}
                  </div>
                )}
              </div>
            </Section>
          )}
          <Section title="Match Reasoning">
            {editing ? <Textarea rows={4} value={form.match_reason || ""} onChange={(e) => setForm({ ...form, match_reason: e.target.value })} /> : <Pre>{cand.match_reason}</Pre>}
          </Section>
          <Section title="Executive Summary">
            {editing ? <Textarea rows={4} value={form.executive_summary || ""} onChange={(e) => setForm({ ...form, executive_summary: e.target.value })} /> : <Pre>{cand.executive_summary}</Pre>}
          </Section>
          <Section title="Risks & Unknowns">
            {editing ? <Textarea rows={3} value={form.risks_unknowns || ""} onChange={(e) => setForm({ ...form, risks_unknowns: e.target.value })} /> : <Pre>{cand.risks_unknowns}</Pre>}
          </Section>
          <Section title="Suggested Next Action">
            {editing ? <Textarea rows={2} value={form.suggested_next_action || ""} onChange={(e) => setForm({ ...form, suggested_next_action: e.target.value })} /> : <Pre>{cand.suggested_next_action}</Pre>}
          </Section>
          <Section title="Review Decision">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Review Status">
                {editing ? (
                  <Select value={form.review_status || ""} onChange={(e) => setForm({ ...form, review_status: e.target.value })}>
                    {["New Candidate","Needs Review","Approved to Import","Rejected","Imported","Archive"].map((o) => <option key={o}>{o}</option>)}
                  </Select>
                ) : <StatusPill value={cand.review_status} />}
              </Field>
              <Field label="Duplicate Check">
                {editing ? (
                  <Select value={form.duplicate_check || ""} onChange={(e) => setForm({ ...form, duplicate_check: e.target.value })}>
                    {["Unique","Possible Duplicate","Duplicate","Unclear"].map((o) => <option key={o}>{o}</option>)}
                  </Select>
                ) : <StatusPill value={cand.duplicate_check} />}
              </Field>
              <Field label="Suggested Fit Score">
                {editing ? <Input type="number" value={form.suggested_fit_score ?? ""} onChange={(e) => setForm({ ...form, suggested_fit_score: Number(e.target.value) })} /> : <div className="text-zinc-200 text-xs font-mono-data">{cand.suggested_fit_score ?? "—"}</div>}
              </Field>
              <Field label="Official Link">
                {editing ? <Input value={form.official_tender_link || ""} onChange={(e) => setForm({ ...form, official_tender_link: e.target.value })} /> :
                  cand.official_tender_link ? <a href={cand.official_tender_link} target="_blank" rel="noreferrer" className="text-[#FFB300] underline text-xs break-all">link</a> : "—"}
              </Field>
            </div>
          </Section>
        </div>
      </div>

      <SourceVerifyModal open={showVerify} candidate={cand} onClose={() => setShowVerify(false)} onSaved={() => load()} />
    </div>
  );
}
function M({ label, value }) {
  return <div><div className="text-zinc-500 text-[9px] tracking-widest uppercase">{label}</div><div className="text-zinc-200 font-mono-data text-[11px]">{value ?? "—"}</div></div>;
}
function ChipGroup({ label, items, mono, tone, testid }) {
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return null;
  const cls = tone === "red" ? "border-[#FF3B30]/40 text-[#FF6B61]" : "border-white/15 text-zinc-300";
  return (
    <div data-testid={testid}>
      <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {arr.map((c, i) => <span key={i} className={`text-[10px] ${mono ? "font-mono-data" : ""} border ${cls} px-2 py-0.5`}>{String(c)}</span>)}
      </div>
    </div>
  );
}
function LinkRow({ label, url, testid }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-zinc-500">{label}</span>
      {url ? <a href={url} target="_blank" rel="noreferrer" className="text-[#FFB300] underline break-all text-right" data-testid={testid}>open</a> : <span className="text-zinc-600">—</span>}
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-300 text-right">{value || "—"}</span>
    </div>
  );
}
function Section({ title, children }) {
  return <section><div className="border-b border-white/10 pb-1.5 mb-3"><h2 className="font-heading text-sm uppercase tracking-[0.2em]">{title}</h2></div>{children}</section>;
}
function Pre({ children }) { return children ? <pre className="whitespace-pre-wrap text-xs text-zinc-300 font-mono-data leading-relaxed">{children}</pre> : <span className="text-zinc-600 italic text-xs">empty</span>; }
